import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { VideoPlayer } from "@/components/common/VideoPlayer"
import { AiGeneratedNotice } from "@/components/common/AiGeneratedNotice"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Copy, Ratio, Layout, Clock, Monitor } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import { localizeName } from "@/lib/i18n-content"
import { useSharedVideo } from "@/hooks/useShare"
import { useTemplate } from "@/hooks/useTemplates"
import { useGeneratorStore } from "@/stores/generatorStore"
import { TemplatePreviewModal } from "@/features/templates/components/TemplatePreviewModal"
import type { SharedVideo } from "@/types/share"
import type { ApplyableSettings, FrameImage } from "@/types/generation"
import { restoreTemplateParameters } from "@/features/templates/lib/restore-template-parameters"

interface CommunityPreviewModalProps {
  video: SharedVideo | null
  onClose: () => void
}

export function CommunityPreviewModal({ video, onClose }: CommunityPreviewModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: sharedDetail } = useSharedVideo(video?.id ?? null)
  const applySettings = useGeneratorStore((state) => state.applySettings)
  const primaryThumbnailUrl = sharedDetail?.thumbnailUrls?.[0]
    ?? sharedDetail?.thumbnailUrl
    ?? video?.thumbnailUrls?.[0]
    ?? video?.thumbnailUrl
    ?? undefined

  // 템플릿 상세 모달
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const { data: selectedTemplate } = useTemplate(selectedTemplateId)

  const handleUseSettings = async () => {
    if (!sharedDetail) return

    const reusableFrameImages: FrameImage[] = (sharedDetail.generation.frameImages ?? []).map((frame, idx) => ({
      id: `reuse-${sharedDetail.generation.id}-${frame.role}-${idx}`,
      role: frame.role,
      s3Key: frame.s3Key,
      thumbnailS3Key: frame.thumbnailS3Key,
      url: sharedDetail.generation.frameImageUrls?.[idx] ?? "",
      thumbnailUrl: sharedDetail.generation.frameImageUrls?.[idx] ?? "",
    }))

    const settings: ApplyableSettings = {
      prompt: sharedDetail.generation.prompt,
      ratio: sharedDetail.generation.ratio as ApplyableSettings['ratio'],
      resolution: sharedDetail.generation.resolution as ApplyableSettings['resolution'],
      duration: sharedDetail.generation.duration,
      frameImages: reusableFrameImages.length > 0 ? reusableFrameImages : undefined,
      template: sharedDetail.generation.template ?? undefined,
    }

    applySettings(await restoreTemplateParameters(settings))
    onClose()
    navigate('/my')
    toast.success(t('community.settingsAppliedToastTitle'), {
      description: t('community.settingsAppliedToastDescription'),
    })
  }

  if (!video) return null

  return (
    <Dialog open={!!video} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-full h-full max-w-none max-h-none sm:max-w-4xl sm:max-h-[90vh] sm:h-auto p-0 gap-0 overflow-y-auto overscroll-contain rounded-none sm:rounded-lg inset-0 sm:inset-auto sm:top-[50%] sm:left-[50%] translate-x-0 translate-y-0 sm:translate-x-[-50%] sm:translate-y-[-50%]">
        <DialogTitle className="sr-only">{t('community.sharedVideoDetail')}</DialogTitle>
        <div className="flex flex-col sm:flex-row min-h-0">
          {/* 좌측: 비디오 */}
          <div className="flex-shrink-0 sm:flex-1 bg-slate-100 flex items-center justify-center p-4 sm:min-h-[500px]">
            <VideoPlayer
              src={video.videoUrl}
              poster={primaryThumbnailUrl}
              controls
              className="max-w-full max-h-full rounded-lg shadow-lg"
              videoClassName="object-contain"
            />
          </div>

          {/* 우측: 정보 패널 */}
          <div className="flex-shrink-0 w-full sm:w-80 bg-white p-6 flex flex-col">
            <AiGeneratedNotice className="mb-4" />
            {/* 프롬프트 */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-500 mb-2">{t('community.prompt')}</h3>
              <div className="max-h-[300px] overflow-y-auto p-3 bg-slate-50 rounded-lg">
                <p className="text-slate-800 text-sm leading-relaxed whitespace-pre-wrap">
                  {sharedDetail?.generation.prompt ?? video.prompt}
                </p>
              </div>
            </div>

            {/* 메타 정보 */}
            <div className="space-y-3 mb-6">
              {/* 비율 */}
              <div className="flex items-center gap-2 text-sm">
                <Ratio className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{t('community.ratioLabel')}</span>
                <span className="text-slate-800 font-medium">
                  {sharedDetail?.generation.ratio ?? video.ratio}
                </span>
              </div>

              {/* 해상도 */}
              {sharedDetail?.generation.resolution && (
                <div className="flex items-center gap-2 text-sm">
                  <Monitor className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{t('community.resolutionLabel')}</span>
                  <span className="text-slate-800 font-medium">
                    {sharedDetail.generation.resolution}
                  </span>
                </div>
              )}

              {/* 길이 */}
              {sharedDetail?.generation.duration && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{t('community.durationLabel')}</span>
                  <span className="text-slate-800 font-medium">
                    {t('common.seconds', { count: sharedDetail.generation.duration })}
                  </span>
                </div>
              )}

              {/* 템플릿 */}
              {sharedDetail?.generation.template && (
                <div className="flex items-center gap-2 text-sm">
                  <Layout className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">{t('community.templateLabel')}</span>
                  <button
                    onClick={() => setSelectedTemplateId(sharedDetail.generation.template!.id)}
                    className="text-slate-800 font-medium hover:text-primary hover:underline transition-colors"
                  >
                    {localizeName(sharedDetail.generation.template)}
                  </button>
                </div>
              )}
            </div>

            {/* 프레임 이미지 */}
            {sharedDetail?.generation.frameImageUrls && sharedDetail.generation.frameImageUrls.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-slate-500 mb-2">{t('community.frameImages')}</h3>
                <div className="flex gap-2">
                  {sharedDetail.generation.frameImageUrls.map((url, idx) => (
                    <div
                      key={idx}
                      className="w-12 h-12 rounded border border-slate-200 overflow-hidden"
                    >
                      <img
                        src={url}
                        alt={t('community.frameImageAlt', { index: idx + 1 })}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 버튼 */}
            <div className="mt-auto pt-4 border-t border-slate-100">
              <Button
                onClick={handleUseSettings}
                className="w-full"
                disabled={!sharedDetail}
              >
                <Copy className="w-4 h-4 mr-2" />
                {t('community.useSettings')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      {/* 템플릿 상세 모달 */}
      <TemplatePreviewModal
        template={selectedTemplate ?? null}
        isOpen={!!selectedTemplateId && !!selectedTemplate}
        onClose={() => setSelectedTemplateId(null)}
      />
    </Dialog>
  )
}
